import { Box, Button, Link, TextField } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useState, useEffect } from "react";
import {
  signIn,
  fetchUserAttributes,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
} from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../utils/config";

const VIEW_LOGIN = "login";
const VIEW_NO_ACCESS = "noAccess";
const VIEW_REQUEST_ACCESS = "requestAccess";
const VIEW_REQUEST_RESULT = "requestResult";
const VIEW_FORGOT_PASSWORD = "forgotPassword";
const VIEW_FORGOT_PASSWORD_SUCCESS = "forgotPasswordSuccess";

const MIN_PASSWORD_LENGTH = 8;
const RESEND_COOLDOWN_SECONDS = 60;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getCognitoErrorMessage = (error, fallback) => {
  switch (error?.name) {
    case "UserNotFoundException":
      return "User not found. Please check your username.";
    case "InvalidPasswordException":
      return error.message || "Password does not meet requirements.";
    case "CodeMismatchException":
      return "Invalid verification code.";
    case "ExpiredCodeException":
      return "Verification code has expired. Please request a new one.";
    case "LimitExceededException":
      return "Too many attempts. Please try again later.";
    default:
      return error?.message || fallback;
  }
};

const Login = () => {
  const navigate = useNavigate();
  const [view, setView] = useState(VIEW_LOGIN);
  const [loginState, setLoginState] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [requestEmail, setRequestEmail] = useState("");
  const [requestEmailError, setRequestEmailError] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);

  const [resetUsername, setResetUsername] = useState("");
  const [resetUsernameError, setResetUsernameError] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [sendCodeLoading, setSendCodeLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      try {
        const session = await fetchAuthSession();
        if (session.tokens?.accessToken) {
          navigate("/dashboard", { replace: true });
        }
      } catch {
        // User is not signed in.
      }
    };
    checkAuthAndRedirect();
  }, [navigate]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => {
      setResendCountdown((current) => current - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const validateEmail = (email) => EMAIL_REGEX.test(email.trim());

  const logLogin = async (result, username) => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      if (result === "Success") {
        const session = await fetchAuthSession();
        if (session.tokens?.accessToken) {
          headers.Authorization = `Bearer ${session.tokens.accessToken.toString()}`;
        }
      }

      await fetch(`${API_URL}/user/log_login`, {
        method: "POST",
        headers,
        body: JSON.stringify({ result, username }),
      });
    } catch (error) {
      console.error("Error logging login status:", error);
    }
  };

  const sendLoginNotification = async (userEmail) => {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.accessToken) {
        return;
      }
      const token = session.tokens.accessToken.toString();
      await fetch("https://api.powerlego.com/generic/send/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer 32a72062e2e878d53d97c829a09335b8`,
        },
        body: JSON.stringify({
          Type: "Common",
          Client: "Constellation-slack",
          Email: "",
          Content: `${userEmail} just logged in to PTC Dashboard`,
          Subject: "PTC Dashboard User Login Notification",
          VerifyToken: token,
        }),
      });
    } catch (error) {
      console.error("Error sending login notification:", error);
    }
  };

  const submitAccessRequest = async (email) => {
    const response = await fetch(`${API_URL}/user/request_access`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email.trim(),
        client: "constellation",
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to submit access request");
    }
  };

  const resetForgotPasswordState = () => {
    setResetUsername("");
    setResetUsernameError("");
    setCodeSent(false);
    setVerificationCode("");
    setNewPassword("");
    setConfirmPassword("");
    setResetError("");
    setSendCodeLoading(false);
    setResetLoading(false);
    setResendCountdown(0);
  };

  const handleBackToLogin = () => {
    setView(VIEW_LOGIN);
    setRequestEmail("");
    setRequestEmailError("");
    setLoginError("");
    resetForgotPasswordState();
  };

  const handleGoToRequestAccess = () => {
    setView(VIEW_REQUEST_ACCESS);
  };

  const handleLogin = async () => {
    const { username, password } = loginState;
    if (!username.trim() || !password) {
      setLoginError("Please enter all required fields");
      return;
    }

    setLoginError("");
    setLoginLoading(true);
    try {
      const auth = await signIn({
        username: username.trim(),
        password,
      });
      if (auth.nextStep.signInStep === "DONE" || auth.isSignedIn) {
        await logLogin("Success", username.trim());
        const userAttributes = await fetchUserAttributes();
        const userEmail = userAttributes.email || username.trim();
        await sendLoginNotification(userEmail);
        navigate("/dashboard");
        return;
      }

      await logLogin("Failed", username.trim());
      setLoginError("Invalid username or password");
    } catch {
      await logLogin("Failed", username.trim());
      setLoginError("Invalid username or password");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRequestAccess = async () => {
    const trimmedEmail = requestEmail.trim();
    if (!trimmedEmail) {
      setRequestEmailError("Please enter your email");
      return;
    }
    if (!validateEmail(trimmedEmail)) {
      setRequestEmailError("Please enter a valid email address");
      return;
    }

    setRequestEmailError("");
    setRequestLoading(true);
    try {
      await submitAccessRequest(trimmedEmail);
      setView(VIEW_REQUEST_RESULT);
    } catch {
      setRequestEmailError("Failed to submit request. Please try again later.");
    } finally {
      setRequestLoading(false);
    }
  };

  const handleSendVerificationCode = async () => {
    const trimmedUsername = resetUsername.trim();
    if (!trimmedUsername) {
      setResetUsernameError("Please enter your username");
      return;
    }

    setResetUsernameError("");
    setResetError("");
    setSendCodeLoading(true);
    try {
      const output = await resetPassword({ username: trimmedUsername });
      const { nextStep } = output;

      if (nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
        setCodeSent(true);
        setResendCountdown(RESEND_COOLDOWN_SECONDS);
        return;
      }

      if (nextStep.resetPasswordStep === "DONE") {
        setView(VIEW_FORGOT_PASSWORD_SUCCESS);
      }
    } catch (error) {
      setResetUsernameError(
        getCognitoErrorMessage(
          error,
          "Failed to send verification code. Please try again later."
        )
      );
    } finally {
      setSendCodeLoading(false);
    }
  };

  const handleResendVerificationCode = async () => {
    if (resendCountdown > 0) return;

    setResetError("");
    setSendCodeLoading(true);
    try {
      const output = await resetPassword({ username: resetUsername.trim() });
      const { nextStep } = output;

      if (nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
        setResendCountdown(RESEND_COOLDOWN_SECONDS);
        return;
      }

      if (nextStep.resetPasswordStep === "DONE") {
        setView(VIEW_FORGOT_PASSWORD_SUCCESS);
      }
    } catch (error) {
      setResetError(
        getCognitoErrorMessage(
          error,
          "Failed to resend verification code. Please try again later."
        )
      );
    } finally {
      setSendCodeLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const trimmedCode = verificationCode.trim();
    if (!trimmedCode) {
      setResetError("Please enter the verification code");
      return;
    }
    if (!newPassword) {
      setResetError("Please enter a new password");
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setResetError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match");
      return;
    }

    setResetError("");
    setResetLoading(true);
    try {
      await confirmResetPassword({
        username: resetUsername.trim(),
        confirmationCode: trimmedCode,
        newPassword,
      });
      setView(VIEW_FORGOT_PASSWORD_SUCCESS);
    } catch (error) {
      setResetError(
        getCognitoErrorMessage(
          error,
          "Failed to reset password. Please try again later."
        )
      );
    } finally {
      setResetLoading(false);
    }
  };

  const renderLoginView = () => (
    <>
      <Box className="w-full mt-5 mb-5">
        <TextField
          className="w-full"
          name="username"
          label="Username"
          variant="outlined"
          value={loginState.username}
          onChange={(e) => {
            setLoginState({ ...loginState, username: e.target.value });
            if (loginError) setLoginError("");
          }}
        />
      </Box>
      <Box className="w-full mt-5 mb-5">
        <TextField
          className="w-full"
          name="password"
          type="password"
          label="Password"
          variant="outlined"
          value={loginState.password}
          onChange={(e) => {
            setLoginState({ ...loginState, password: e.target.value });
            if (loginError) setLoginError("");
          }}
        />
      </Box>
      <Box className="mt-5 w-full">
        <Button
          className="w-full"
          variant="contained"
          disabled={loginLoading}
          onClick={handleLogin}
        >
          {loginLoading ? "Signing in..." : "Sign In"}
        </Button>
        {loginError && (
          <p className="text-red-600 text-sm mt-2">{loginError}</p>
        )}
      </Box>
      <Box className="mt-4 w-full flex flex-col items-center gap-1">
        <Button
          variant="text"
          onClick={() => {
            resetForgotPasswordState();
            setView(VIEW_FORGOT_PASSWORD);
          }}
        >
          Forgot Password?
        </Button>
        <Button variant="text" onClick={() => setView(VIEW_REQUEST_ACCESS)}>
          Don&apos;t have an account? Request Access
        </Button>
      </Box>
    </>
  );

  const renderNoAccessView = () => (
    <Box
      className="w-full p-8 bg-white rounded-lg flex flex-col items-center text-center"
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        boxShadow:
          "0 0 0 1px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.06), 0 -2px 8px rgba(0, 0, 0, 0.06)",
      }}
    >
      <HighlightOffIcon sx={{ fontSize: 56, color: "error.main", mb: 2 }} />
      <p className="text-xl font-bold text-gray-900 mb-2">
        You don&apos;t have access to this.
      </p>
      <p className="text-sm text-gray-600 leading-relaxed mb-6">
        The email address you entered isn&apos;t on our approved list.
      </p>
      <Box sx={{ position: "relative", display: "inline-flex", mb: 6 }}>
        <MailOutlineIcon sx={{ fontSize: 88, color: "grey.400" }} />
        <LockOutlinedIcon
          sx={{
            fontSize: 28,
            color: "grey.500",
            position: "absolute",
            bottom: 4,
            right: -10,
            bgcolor: "white",
            borderRadius: "50%",
            p: 0.25,
          }}
        />
      </Box>
      <p className="text-sm text-gray-600 leading-relaxed mb-6">
        If you believe you should have access, please request access.
      </p>
      <Button
        className="w-full"
        variant="contained"
        onClick={handleGoToRequestAccess}
        sx={{ mb: 2, py: 1.25 }}
      >
        Request Access
      </Button>
      <Link
        component="button"
        variant="body2"
        onClick={handleBackToLogin}
        className="cursor-pointer uppercase tracking-wide"
      >
        Back to Login
      </Link>
    </Box>
  );

  const renderRequestAccessView = () => (
    <>
      <Box className="w-full mb-5">
        <p className="text-sm text-gray-600 leading-relaxed">
          Please enter your email. We will send a confirmation to your email
          within 3-5 business days after receiving your request.
        </p>
      </Box>
      <Box className="w-full mt-5 mb-5">
        <TextField
          className="w-full"
          name="email"
          label="Email"
          type="email"
          variant="outlined"
          value={requestEmail}
          error={Boolean(requestEmailError)}
          helperText={requestEmailError}
          onChange={(e) => {
            setRequestEmail(e.target.value);
            if (requestEmailError) {
              setRequestEmailError("");
            }
          }}
        />
      </Box>
      <Box className="mt-5 w-full">
        <Button
          className="w-full"
          variant="contained"
          disabled={requestLoading}
          onClick={handleRequestAccess}
        >
          {requestLoading ? "Submitting..." : "Confirm"}
        </Button>
      </Box>
      <Box className="mt-4 w-full flex justify-center">
        <Link
          component="button"
          variant="body2"
          onClick={handleBackToLogin}
          className="cursor-pointer"
        >
          Back to Login
        </Link>
      </Box>
    </>
  );

  const renderForgotPasswordView = () => (
    <>
      <Box className="w-full mb-5">
        <p className="text-sm text-gray-600 leading-relaxed">
          {codeSent
            ? "Enter the verification code sent to your email and choose a new password."
            : "Enter your username and we will send you a verification code."}
        </p>
      </Box>
      <Box className="w-full mt-5 mb-5">
        <TextField
          className="w-full"
          name="resetUsername"
          label="Username"
          variant="outlined"
          value={resetUsername}
          error={Boolean(resetUsernameError)}
          helperText={resetUsernameError}
          disabled={codeSent}
          onChange={(e) => {
            setResetUsername(e.target.value);
            if (resetUsernameError) {
              setResetUsernameError("");
            }
          }}
        />
      </Box>
      {!codeSent ? (
        <Box className="mt-5 w-full">
          <Button
            className="w-full"
            variant="contained"
            disabled={sendCodeLoading}
            onClick={handleSendVerificationCode}
          >
            {sendCodeLoading ? "Sending..." : "Send Verification Code"}
          </Button>
        </Box>
      ) : (
        <>
          <Box className="w-full mt-5 mb-5 flex gap-2 items-start">
            <TextField
              className="flex-1"
              name="verificationCode"
              label="Verification Code"
              variant="outlined"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value);
                if (resetError) setResetError("");
              }}
            />
            <Button
              variant="outlined"
              disabled={resendCountdown > 0 || sendCodeLoading}
              onClick={handleResendVerificationCode}
              sx={{ minWidth: 120, height: 56 }}
            >
              {resendCountdown > 0 ? `${resendCountdown}s` : "Resend"}
            </Button>
          </Box>
          <Box className="w-full mt-5 mb-5">
            <TextField
              className="w-full"
              name="newPassword"
              type="password"
              label="New Password"
              variant="outlined"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (resetError) setResetError("");
              }}
            />
          </Box>
          <Box className="w-full mt-5 mb-5">
            <TextField
              className="w-full"
              name="confirmPassword"
              type="password"
              label="Confirm New Password"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (resetError) setResetError("");
              }}
            />
          </Box>
          <Box className="mt-5 w-full">
            <Button
              className="w-full"
              variant="contained"
              disabled={resetLoading}
              onClick={handleResetPassword}
            >
              {resetLoading ? "Submitting..." : "Reset Password"}
            </Button>
            {resetError && (
              <p className="text-red-600 text-sm mt-2">{resetError}</p>
            )}
          </Box>
        </>
      )}
      <Box className="mt-4 w-full flex justify-center">
        <Link
          component="button"
          variant="body2"
          onClick={handleBackToLogin}
          className="cursor-pointer"
        >
          Back to Login
        </Link>
      </Box>
    </>
  );

  const renderForgotPasswordSuccessView = () => (
    <>
      <Box className="w-full mb-5 flex flex-col items-center py-6">
        <CheckCircleOutlineIcon
          sx={{ fontSize: 72, color: "success.main", mb: 2 }}
        />
        <p className="text-sm text-gray-600 text-center leading-relaxed">
          Your password has been reset successfully. You can now sign in with
          your new password.
        </p>
      </Box>
      <Box className="mt-4 w-full">
        <Button
          className="w-full"
          variant="contained"
          onClick={handleBackToLogin}
        >
          Back to Login
        </Button>
      </Box>
    </>
  );

  const renderRequestResultView = () => (
    <>
      <Box className="w-full mb-5 flex flex-col items-center py-6">
        <CheckCircleOutlineIcon
          sx={{ fontSize: 72, color: "success.main", mb: 2 }}
        />
        <p className="text-sm text-gray-600 text-center leading-relaxed">
          Your access request has been submitted. We will send a confirmation to
          your email within 3-5 business days.
        </p>
      </Box>
      <Box className="mt-4 w-full flex justify-center">
        <Link
          component="button"
          variant="body2"
          onClick={handleBackToLogin}
          className="cursor-pointer"
        >
          Back to Login
        </Link>
      </Box>
    </>
  );

  const viewTitles = {
    [VIEW_LOGIN]: "Sign In",
    [VIEW_NO_ACCESS]: "",
    [VIEW_REQUEST_ACCESS]: "Request Access",
    [VIEW_REQUEST_RESULT]: "Request Submitted",
    [VIEW_FORGOT_PASSWORD]: "Reset Password",
    [VIEW_FORGOT_PASSWORD_SUCCESS]: "Password Reset",
  };

  return (
    <Box className="h-[100vh] flex  justify-center">
      <Box className="pt-10 min-w-[400px]">
        <Box className="w-full p-5 flex justify-center">
          {viewTitles[view] && (
            <p className="font-bold text-2xl">{viewTitles[view]}</p>
          )}
        </Box>
        {view === VIEW_LOGIN && renderLoginView()}
        {view === VIEW_NO_ACCESS && renderNoAccessView()}
        {view === VIEW_REQUEST_ACCESS && renderRequestAccessView()}
        {view === VIEW_REQUEST_RESULT && renderRequestResultView()}
        {view === VIEW_FORGOT_PASSWORD && renderForgotPasswordView()}
        {view === VIEW_FORGOT_PASSWORD_SUCCESS &&
          renderForgotPasswordSuccessView()}
      </Box>
    </Box>
  );
};

export default Login;
